import { ExternalLink, X } from "lucide-react";
import { toast } from "sonner";
import { api, short, usd, signedUsd, pct, price, tone, ago } from "@/lib/api";

const STATUS = {
  open: ["OPEN", "border-[#06B6D4]/50 text-[#06B6D4] bg-[#06B6D4]/10"],
  tp: ["TP HIT", "border-[#14F195]/50 text-[#14F195] bg-[#14F195]/10"],
  trail: ["TRAIL TP", "border-[#14F195]/50 text-[#14F195] bg-[#14F195]/10"],
  sl: ["SL HIT", "border-[#EF4444]/50 text-[#EF4444] bg-[#EF4444]/10"],
  manual: ["CLOSED", "border-slate-600 text-slate-400"],
  mirror: ["MIRROR SOLD", "border-[#9945FF]/50 text-[#c4a1ff] bg-[#9945FF]/10"],
  failed: ["FAILED", "border-[#F59E0B]/50 text-[#F59E0B] bg-[#F59E0B]/10"],
  pending: ["BUYING…", "border-[#F59E0B]/50 text-[#F59E0B] bg-[#F59E0B]/10"],
  closing: ["SELLING…", "border-[#F59E0B]/50 text-[#F59E0B] bg-[#F59E0B]/10"],
};

const Fill = ({ t }) => {
  if (t.mode !== "live") return <span className="text-[10px] text-slate-500" data-testid={`trade-fill-${t.id}`}>simulated</span>;
  const leg = (name, sig, ok, fill) => {
    if (!sig) return null;
    if (ok === null || ok === undefined)
      return <div className="flex items-center gap-1 text-[#F59E0B]"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] pulse-dot" />{name} confirming</div>;
    if (!ok) return <div className="text-[#EF4444]">{name} failed</div>;
    return (
      <div>
        <span className="px-1.5 py-px border border-[#14F195]/50 text-[#14F195] bg-[#14F195]/10 text-[9px] tracking-wider">{name.toUpperCase()} CONFIRMED</span>
        {fill && <div className="text-slate-400 mt-0.5">{(fill.tokens || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} · {(fill.sol || 0).toFixed(5)} SOL</div>}
      </div>
    );
  };
  return (
    <div className="text-[10px] space-y-1" data-testid={`trade-fill-${t.id}`}>
      {leg("buy", t.buy_signature, t.buy_confirmed, t.buy_fill)}
      {leg("sell", t.sell_signature, t.sell_confirmed, t.sell_fill)}
      {t.status === "pending" && !t.buy_signature && <div className="text-[#F59E0B]" data-testid={`trade-pending-${t.id}`}>waiting for on-chain confirmation</div>}
      {t.error && <div className="text-[#EF4444] max-w-[220px] break-words" title={t.error} data-testid={`trade-error-${t.id}`}>{t.error}</div>}
      {!t.buy_signature && !t.error && t.status !== "pending" && <span className="text-slate-500">—</span>}
    </div>
  );
};

const TxLink = ({ sig, label }) =>
  sig ? (
    <a href={`https://solscan.io/tx/${sig}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate-400 hover:text-[#14F195] transition-colors">
      {label} <ExternalLink className="w-3 h-3" />
    </a>
  ) : null;

const Row = ({ t, refresh }) => {
  const [label, cls] = STATUS[t.status] || STATUS.manual;
  const close = async () => {
    try {
      await api.post(`/trades/${t.id}/close`);
      toast.success(`Closed ${t.symbol}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close failed");
    }
  };
  return (
    <tr className="border-b border-[#1E293B]/60 hover:bg-[#111827] transition-colors" data-testid={`trade-row-${t.id}`}>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2.5">
          {t.icon ? <img src={t.icon} alt="" className="w-6 h-6 rounded-full bg-[#1E293B]" /> : <div className="w-6 h-6 rounded-full bg-[#1E293B]" />}
          <div>
            <a href={`https://gmgn.ai/sol/token/${t.mint}`} target="_blank" rel="noreferrer" className="text-slate-100 font-bold hover:text-[#14F195] transition-colors">{t.symbol}</a>
            <div className="text-[10px] text-slate-500">{short(t.mint)} · {t.mode} · <span data-testid={`trade-venue-${t.id}`} className={t.venue?.startsWith("pump") ? "text-[#14F195]" : ""}>{{ pump_curve: "pump.fun", pump_pool: "PumpSwap" }[t.venue] || "Raydium"}</span></div>
          </div>
        </div>
      </td>
      <td className="px-2 py-3 text-slate-400">{t.trader_label ? `@${t.trader_label}` : short(t.trader)}</td>
      <td className="px-2 py-3 text-right text-slate-300" data-testid={`trade-entry-${t.id}`}>{price(t.entry_price)}</td>
      <td className="px-2 py-3 text-right text-slate-300">{price(t.current_price)}</td>
      <td className="px-2 py-3 text-right text-slate-100" data-testid={`trade-value-${t.id}`}>
        {usd(t.current_value)}<div className="text-[10px] text-slate-500">cost {usd(t.cost_usd)}</div>
      </td>
      <td className={`px-2 py-3 text-right font-bold ${tone(t.pnl_usd)}`} data-testid={`trade-pnl-${t.id}`}>
        {signedUsd(t.pnl_usd)}<div className="text-[10px] font-normal">{pct(t.pnl_pct)}</div>
      </td>
      <td className="px-2 py-3">
        <span className={`px-2 py-0.5 border text-[10px] tracking-wider ${cls}`} title={t.error || ""}>{label}</span>
        {t.status === "open" && !t.exit_stuck && (
          <div className="mt-1 text-[10px] text-slate-400" data-testid={`trade-exit-${t.id}`}>
            sells when trader sells{t.peak_pct != null && <div className="text-slate-500">peak {pct(t.peak_pct)}</div>}
          </div>
        )}
      </td>
      <td className="px-2 py-3"><Fill t={t} /></td>
      <td className="px-2 py-3 text-[10px]">
        <div className="flex flex-col gap-0.5">
          <TxLink sig={t.buy_signature} label="buy" />
          <TxLink sig={t.sell_signature} label="sell" />
          <TxLink sig={t.source_signature} label="source" />
        </div>
      </td>
      <td className="px-5 py-3 text-right">
        <div className="text-[10px] text-slate-500">{ago(t.opened_at)}</div>
        {t.status === "open" && (
          <button data-testid={`trade-close-${t.id}`} onClick={close} className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-[#EF4444] transition-colors">
            <X className="w-3 h-3" /> sell
          </button>
        )}
      </td>
    </tr>
  );
};

export const TradesTable = ({ trades, refresh }) => (
  <div className="panel rise" style={{ animationDelay: "160ms" }} data-testid="trades-table">
    <div className="px-5 py-4 border-b border-[#1E293B] flex items-center justify-between">
      <div className="panel-title">Copied trades · positions</div>
      <div className="text-[10px] font-mono text-slate-500">prices refresh every 15s · each copy sells the moment its trader sells</div>
    </div>
    <div className="overflow-auto scroll-thin">
      <table className="w-full text-xs font-mono min-w-[1020px]">
        <thead className="text-slate-500 text-[10px] uppercase tracking-wider">
          <tr className="border-b border-[#1E293B]">
            {["Token", "Copied from", "Entry", "Current", "Value", "PnL", "Status", "Fill", "Tx", "Opened"].map((h, i) => (
              <th key={h} className={`py-2.5 font-medium ${i === 0 ? "px-5 text-left" : i === 9 ? "px-5 text-right" : [2, 3, 4, 5].includes(i) ? "px-2 text-right" : "px-2 text-left"}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.length === 0 && (
            <tr><td colSpan={10} className="px-5 py-12 text-center text-slate-500" data-testid="trades-empty">
              Waiting for a top trader to buy. The engine watches their wallets every 2 minutes and copies automatically.
            </td></tr>
          )}
          {trades.map((t) => <Row key={t.id} t={t} refresh={refresh} />)}
        </tbody>
      </table>
    </div>
  </div>
);
